import { Injectable, OnDestroy } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Subject } from 'rxjs/Subject';
import { environment } from '../../../../../../environments/environment';
import { JwtUtils } from '../../../../../auth/utilities/jwtUtils';
import { ConsoleLogger } from '../../../../utilities/console-logger';
import { HubReconnectScheduler } from '../../../../utilities/hub-reconnect-scheduler';
import { Division } from '../../../../constants/division';

@Injectable()
export class SideNavSignalRService implements OnDestroy {
  private static instance: SideNavSignalRService;
  private connection: signalR.HubConnection;
  private queueCountSource = new BehaviorSubject<number>(0);
  queueCount$ = this.queueCountSource.asObservable();
  private queueCountsByType = new Map<string, BehaviorSubject<number>>();
  private notificationCountSource = new BehaviorSubject<number>(0);
  notificationCount$ = this.notificationCountSource.asObservable();
  private notificationCountByDivision = new Map<number, BehaviorSubject<number>>();
  private errorCountSource = new BehaviorSubject<number>(0);
  errorCount$ = this.errorCountSource.asObservable();
  private errorCountByDivision = new Map<number, BehaviorSubject<number>>();
  private reminderCountSource = new BehaviorSubject<number>(0);
  reminderCount$ = this.reminderCountSource.asObservable();
  private reconnectedSource = new Subject<void>();
  reconnected$ = this.reconnectedSource.asObservable();
  private isConnected = false;
  private isInitializing = false;
  private hasJoinedGroup = false;
  private hasPendingReconnect = false;
  private isShuttingDown = false;
  private reconnectScheduler = new HubReconnectScheduler('SideNavHub', () =>
    this.initializeConnection()
  );
  private joinScheduler = new HubReconnectScheduler('SideNavHub group join', () =>
    this.joinQueueGroup()
  );

  constructor() {
    if (SideNavSignalRService.instance) {
      ConsoleLogger.logInfo('Reusing existing SideNavHub instance');
      return SideNavSignalRService.instance;
    }
    
    SideNavSignalRService.instance = this;
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.initializeConnection();
  }

  private handleVisibilityChange = () => {
    if (document.hidden) {
      return;
    }

    if (!this.connection || this.connection.state === signalR.HubConnectionState.Disconnected) {
      ConsoleLogger.logInfo('SideNavHub disconnected on tab resume, reconnecting');
      this.reconnectScheduler.reset();
      this.initializeConnection();
      return;
    }

    if (!this.hasJoinedGroup) {
      this.joinQueueGroup();
    }
  };
  
  ngOnDestroy() {
    this.disconnect();
  }

  initializeConnection() {
    ConsoleLogger.logInfo('Initializing SideNavHub connection');
    if (this.isConnected || this.isInitializing) {
      ConsoleLogger.logInfo('SideNavHub connection already established or initializing, skipping initialization');
      return;
    }
    
    this.isInitializing = true;
    this.isShuttingDown = false;

    const connectionOptions: signalR.IHttpConnectionOptions = {
      skipNegotiation: true,
      transport: signalR.HttpTransportType.WebSockets,
      accessTokenFactory: () => JwtUtils.accessToken
    };

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(environment.sideNavHubUrl, connectionOptions)
      .withAutomaticReconnect([0, 1000, 2000, 5000, 10000, 30000])
      .build();

    // 2x the server's 30s KeepAliveInterval
    this.connection.serverTimeoutInMilliseconds = 60000;

    this.registerEventHandlers();

    this.connection.start()
      .then(() => {
        ConsoleLogger.logInfo('SideNavHub connection established');
        this.isInitializing = false;

        if (this.isShuttingDown) {
          this.connection.stop()
            .catch(err => ConsoleLogger.logInfo('Error stopping SideNavHub after disconnect:', err));
          return;
        }

        this.isConnected = true;
        this.reconnectScheduler.reset();
        this.joinQueueGroup();
      })
      .catch(err => {
        console.warn('Error establishing SideNavHub connection:', err);
        this.isConnected = false;
        this.isInitializing = false;

        if (!this.isShuttingDown) {
          this.reconnectScheduler.schedule();
        }
      });
  }

  private registerEventHandlers() {
    this.connection.onreconnected(() => {
      ConsoleLogger.logInfo('SideNavHub connection reconnected');
      this.isConnected = true;
      this.reconnectScheduler.reset();
      this.hasPendingReconnect = true;
      this.joinQueueGroup();
    });

    this.connection.onreconnecting(() => {
      ConsoleLogger.logInfo('SideNavHub connection reconnecting');
      this.isConnected = false;
    });

    this.connection.onclose(() => {
      ConsoleLogger.logInfo('SideNavHub connection closed');
      this.isConnected = false;
      this.hasJoinedGroup = false;
      this.isInitializing = false;

      if (!this.isShuttingDown) {
        this.hasPendingReconnect = true;
        this.reconnectScheduler.schedule();
      }
    });

    this.connection.on('UpdateQueueCount', (count: number) => {
      this.queueCountSource.next(count);
    });

    this.connection.on('UpdateQueueCountByType', (queueType: string, count: number) => {
      ConsoleLogger.logInfo(`Queue count updated for ${queueType}: ${count}`);
      this.getQueueCountSubject(queueType).next(count);
    });

    this.connection.on('UpdateNotificationCount', (count: number) => {
      this.notificationCountSource.next(count);
    });
    
    this.connection.on('UpdateNotificationCountByDivision', (divisionId: number, count: number) => {
      ConsoleLogger.logInfo(`Notification count updated for division ${divisionId}: ${count}`);
      this.getNotificationCountByDivisionSubject(divisionId).next(count);
    });

    this.connection.on('UpdateNotificationErrorCount', (count: number) => {
      this.errorCountSource.next(count);
    });

    this.connection.on('UpdateNotificationErrorCountByDivision', (divisionId: number, count: number) => {
      ConsoleLogger.logInfo(`Error count updated for division ${divisionId}: ${count}`);
      this.getErrorCountByDivisionSubject(divisionId).next(count);
    });

    this.connection.on('UpdateReminderCount', (count: number) => {
      this.reminderCountSource.next(count);
    });
  }

  private joinQueueGroup() {
    if (!this.connection || this.connection.state !== signalR.HubConnectionState.Connected) {
      return Promise.resolve();
    }

    return this.connection.invoke('JoinQueueGroup')
      .then(() => {
        this.hasJoinedGroup = true;
        this.joinScheduler.reset();

        if (this.hasPendingReconnect) {
          this.hasPendingReconnect = false;
          this.reconnectedSource.next();
        }
      })
      .catch(err => {
        ConsoleLogger.logInfo('Error joining queue group:', err);
        this.hasJoinedGroup = false;
        this.joinScheduler.schedule();
      });
  }

  public getQueueCountByType(queueType: string) {
    return this.getQueueCountSubject(queueType).asObservable();
  }

  private getQueueCountSubject(queueType: string): BehaviorSubject<number> {
    if (!this.queueCountsByType.has(queueType)) {
      this.queueCountsByType.set(queueType, new BehaviorSubject<number>(0));
    }
    return this.queueCountsByType.get(queueType);
  }

  public getNotificationCount() {
    return this.notificationCount$;
  }
  
  public getNotificationCountByDivision(divisionId: number) {
    return this.getNotificationCountByDivisionSubject(divisionId).asObservable();
  }
  
  private getNotificationCountByDivisionSubject(divisionId: number): BehaviorSubject<number> {
    if (!this.notificationCountByDivision.has(divisionId)) {
      this.notificationCountByDivision.set(divisionId, new BehaviorSubject<number>(0));
    }
    return this.notificationCountByDivision.get(divisionId);
  }
  
  public getTransportationNotificationCount() {
    return this.getNotificationCountByDivision(Division.Transportation);
  }
  
  public getFactoringNotificationCount() {
    return this.getNotificationCountByDivision(Division.Factoring);
  }

  public getErrorCount() {
    return this.errorCount$;
  }

  public getErrorCountByDivision(divisionId: number) {
    return this.getErrorCountByDivisionSubject(divisionId).asObservable();
  }
  
  private getErrorCountByDivisionSubject(divisionId: number): BehaviorSubject<number> {
    if (!this.errorCountByDivision.has(divisionId)) {
      this.errorCountByDivision.set(divisionId, new BehaviorSubject<number>(0));
    }
    return this.errorCountByDivision.get(divisionId);
  }
  
  public getTransportationErrorCount() {
    return this.getErrorCountByDivision(Division.Transportation);
  }
  
  public getFactoringErrorCount() {
    return this.getErrorCountByDivision(Division.Factoring);
  }

  public getReminderCount() {
    return this.reminderCount$;
  }

  public disconnect() {
    this.isShuttingDown = true;
    this.reconnectScheduler.cancel();
    this.joinScheduler.cancel();
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    this.hasJoinedGroup = false;

    if (this.connection) {
      this.connection.stop()
        .then(() => {
          this.isConnected = false;
          this.isInitializing = false;
          ConsoleLogger.logInfo('SideNavHub connection stopped');
        })
        .catch(err => {
          this.isConnected = false;
          this.isInitializing = false;
          ConsoleLogger.logInfo('Error stopping SideNavHub connection:', err);
        });
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/core/components/menu/side-nav/side-nav-item/side-nav-signalr.service.ts